'use client';

import {
  MarcadorSecao as MarcadorSecaoDocumento,
  type SecaoDocumento,
  SumarioDocumento,
} from '@/shared/components/documentos/paginado';

export type SecaoCertificado = SecaoDocumento;

// Ordem canônica das seções do certificado. Usada tanto no sumário (topo)
// quanto nos marcadores de início de cada seção, para ficarem sempre em sincronia.
export const SECOES_CERTIFICADO: SecaoCertificado[] = [
  {
    numero: 1,
    titulo: 'Partes envolvidas',
    descricao: 'Requerente, requeridos e advogados do processo',
  },
  {
    numero: 2,
    titulo: 'Objetivo e escopo',
    descricao: 'O que é este documento e o que ele comprova',
  },
  {
    numero: 3,
    titulo: 'Situação da comprovação por parte',
    descricao: 'Quem já tomou ciência, por canal de comunicação',
  },
  {
    numero: 4,
    titulo: 'Segurança do documento',
    descricao: 'Como os registros são protegidos contra adulteração',
  },
  {
    numero: 5,
    titulo: 'Detalhes de auditoria',
    descricao: 'Cada tentativa e evento, com data, hora e metadados',
  },
  {
    numero: 6,
    titulo: 'Comunicados enviados',
    descricao: 'Cópias íntegras das mensagens de WhatsApp e e-mail enviadas',
  },
  {
    numero: 7,
    titulo: 'Documentos dos Correios',
    descricao: 'Cartas de citação, comprovantes de postagem e AR',
  },
];

/** Seção 7 — a única do certificado que pode não existir na emissão (ver `secoesCertificado`). */
export const SECAO_DOCUMENTOS_CORREIOS = 7;

/**
 * As seções desta emissão.
 *
 * Sem carta postada não há "Documentos dos Correios": nem divisória, nem linha no sumário (QA
 * itens 6 e 10a). A divisória sozinha ocupava uma página inteira prometendo anexo inexistente, e
 * tirá-la só do corpo deixaria o índice apontando para nada — por isso a decisão mora aqui, na
 * lista que alimenta os dois.
 *
 * É a ÚLTIMA da ordem canônica, então escondê-la não renumera nenhuma outra. O que muda é o total
 * exibido ("Seção 1 de 6"), derivado de `secoes.length` pelo design system.
 */
export function secoesCertificado(temDocumentosCorreios: boolean): readonly SecaoCertificado[] {
  if (temDocumentosCorreios) return SECOES_CERTIFICADO;
  return SECOES_CERTIFICADO.filter((secao) => secao.numero !== SECAO_DOCUMENTOS_CORREIOS);
}

const DESCRICAO_SUMARIO_CERTIFICADO = (totalSecoes: number) =>
  `O documento está organizado em ${totalSecoes} seções, na ordem abaixo. O início de cada uma é marcado por uma divisória numerada. Clique em uma seção para ir direto até ela.`;

interface SumarioCertificadoProps {
  secoes?: readonly SecaoCertificado[];
}

export function SumarioCertificado({ secoes = SECOES_CERTIFICADO }: SumarioCertificadoProps) {
  return (
    <SumarioDocumento
      titulo="O que este certificado contém"
      descricao={DESCRICAO_SUMARIO_CERTIFICADO(secoes.length)}
      secoes={secoes}
    />
  );
}

interface MarcadorSecaoProps {
  numero: number;
  secoes?: readonly SecaoCertificado[];
}

export function MarcadorSecao({ numero, secoes = SECOES_CERTIFICADO }: MarcadorSecaoProps) {
  return <MarcadorSecaoDocumento numero={numero} secoes={secoes} />;
}
